import React, { useEffect, useState } from 'react';
import ClientPortalLayout from './Layout';
import { clientPortalFetch } from './api';
import { formatCurrency, formatDate, StatusPill } from './Dashboard';
import { buildInvoiceHtml, buildReceiptHtml, openPrintWindow } from '../../utils/invoiceDocument';

const NAVY = '#0f1f45';

// Uses the exact same official template as the staff-facing Finance module
// (see src/utils/invoiceDocument.js) so a client's downloaded PDF matches
// what the firm actually configured — branding, GST breakdown, bank/QR,
// custom footer — not a simplified stand-in.
const printInvoice = (inv) => openPrintWindow(buildInvoiceHtml(inv));

// A client-portal receipt can settle against multiple invoices (no single
// "parent invoice" the way Finance.js's per-invoice print has), so there's
// no coherent single balance-due figure to show — the settlement table
// already lists each invoice's own amount, so the ledger-balance line is
// deliberately turned off here rather than showing a misleading number.
const printReceipt = (rec) => {
  const invoiceLike = { ...rec, show_receipt_ledger: false };
  const paymentLike = { ...rec, amount: rec.received_amount };
  openPrintWindow(buildReceiptHtml(invoiceLike, paymentLike));
};

function InvoicesInner() {
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    Promise.all([
      clientPortalFetch('/invoices').then(setInvoices).catch(() => {}),
      clientPortalFetch('/receipts').then(setReceipts).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handlePrintInvoice = async (id) => {
    setBusyId(id);
    try {
      const data = await clientPortalFetch(`/invoices/${id}`);
      printInvoice(data);
    } catch (e) { alert(e.message); }
    setBusyId(null);
  };

  const handlePrintReceipt = async (id) => {
    setBusyId(id);
    try {
      const data = await clientPortalFetch(`/receipts/${id}`);
      printReceipt(data);
    } catch (e) { alert(e.message); }
    setBusyId(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <ToggleBtn active={tab === 'invoices'} onClick={() => setTab('invoices')}>Invoices</ToggleBtn>
        <ToggleBtn active={tab === 'receipts'} onClick={() => setTab('receipts')}>Receipts</ToggleBtn>
      </div>

      {loading && <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>}

      {!loading && tab === 'invoices' && (
        invoices.length === 0 ? <Empty text="No invoices yet." /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {invoices.map((inv) => (
              <Row key={inv.id}
                title={inv.invoice_number}
                subtitle={formatDate(inv.invoice_date)}
                amount={formatCurrency(inv.total_amount)}
                status={<StatusPill status={inv.status} />}
                onDownload={() => handlePrintInvoice(inv.id)}
                busy={busyId === inv.id}
              />
            ))}
          </div>
        )
      )}

      {!loading && tab === 'receipts' && (
        receipts.length === 0 ? <Empty text="No receipts yet." /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {receipts.map((rec) => (
              <Row key={rec.id}
                title={rec.receipt_number}
                subtitle={`${formatDate(rec.payment_date)}${rec.mode ? ` · ${rec.mode}` : ''}`}
                amount={formatCurrency(rec.received_amount)}
                onDownload={() => handlePrintReceipt(rec.id)}
                busy={busyId === rec.id}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

const Row = ({ title, subtitle, amount, status, onDownload, busy }) => (
  <div style={{
    background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  }}>
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{amount}</div>
      {status}
      <button onClick={onDownload} disabled={busy} style={{
        width: 34, height: 34, borderRadius: 8, border: 'none', background: '#eff6ff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'not-allowed' : 'pointer',
      }}>
        <svg width="15" height="15" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
        </svg>
      </button>
    </div>
  </div>
);

const Empty = ({ text }) => (
  <div style={{ padding: '48px 0', textAlign: 'center', background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
    <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>{text}</p>
  </div>
);

const ToggleBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{
    border: '1px solid #e5e7eb', borderRadius: 999, padding: '7px 16px', fontSize: 12.5, fontWeight: 500,
    cursor: 'pointer', background: active ? NAVY : '#fff', color: active ? '#fff' : '#374151',
  }}>
    {children}
  </button>
);

function Invoices() {
  return <ClientPortalLayout title="Invoices"><InvoicesInner /></ClientPortalLayout>;
}

export default Invoices;
